/** Formatting + small browser helpers shared by the WMS screens and prints. */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const pad2 = (n: number) => String(n).padStart(2, '0');

const toDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  // A bare calendar day (YYYY-MM-DD) is a local day, not UTC midnight.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso)
    ? new Date(`${iso}T00:00:00`)
    : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** `07 Oct 2026` */
export const fmtDate = (iso: string | null | undefined): string => {
  const d = toDate(iso);
  if (!d) return '—';
  return `${pad2(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/** `07 Oct, 14:05` */
export const fmtDateTime = (iso: string | null | undefined): string => {
  const d = toDate(iso);
  if (!d) return '—';
  return `${pad2(d.getDate())} ${MONTHS[d.getMonth()]}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** `07 Oct 2026, 14:05` — for print, where the year matters. */
export const fmtDateTimeLong = (iso: string | null | undefined): string => {
  const d = toDate(iso);
  if (!d) return '—';
  return `${fmtDate(iso)}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

export const fmtQty = (n: number | null | undefined): string => {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '0';
  return Number.isInteger(v)
    ? v.toLocaleString('id-ID')
    : v.toLocaleString('id-ID', { maximumFractionDigits: 2 });
};

/** Road distance in metres → "850 m" / "8,4 km"; null → "—". */
export const fmtDistance = (metres: number | null | undefined): string => {
  if (metres === null || metres === undefined || !Number.isFinite(metres))
    return '—';
  if (metres < 1000) return `${Math.round(metres).toLocaleString('id-ID')} m`;
  return `${(metres / 1000).toLocaleString('id-ID', {
    maximumFractionDigits: 1,
  })} km`;
};

export const sum = (xs: number[]): number =>
  xs.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);

/** Whole days from now until `iso` (negative = past). */
export const daysUntil = (iso: string | null | undefined): number | null => {
  const d = toDate(iso);
  if (!d) return null;
  return Math.round((d.getTime() - Date.now()) / 86_400_000);
};

export const isExpired = (iso: string | null | undefined): boolean => {
  const n = daysUntil(iso);
  return n !== null && n < 0;
};

/** Today's calendar day in Asia/Jakarta (the TRD validates against it). */
export const todayJakarta = (): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Jakarta',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

/** `YYYY-MM-DD` n days after today (Jakarta). */
export const addDays = (isoDay: string, n: number): string => {
  const d = new Date(`${isoDay}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** `YYYY-MM-DD` of an ISO instant, as the local calendar day (null / junk → ''). */
export const localDay = (iso: string | null | undefined): string => {
  const d = toDate(iso);
  if (!d) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** Local `datetime-local` value for "now". */
export const nowLocalInput = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/* -------------------------------- CSV -------------------------------- */

/**
 * One CSV cell. Cells starting with `= + - @` (or a tab / CR) are prefixed
 * with `'` so a spreadsheet never evaluates them as formulas (CSV injection),
 * then quoted when they carry a delimiter, quote or newline.
 */
export const csvCell = (value: unknown): string => {
  let s = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const toCsv = (rows: unknown[][]): string =>
  rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';

/** Trigger a browser download for a blob. */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** UTF-8 BOM so Excel opens Indonesian text correctly. */
export const downloadCsv = (filename: string, rows: unknown[][]): void =>
  downloadBlob(
    new Blob(['﻿' + toCsv(rows)], { type: 'text/csv;charset=utf-8' }),
    filename
  );

/**
 * Case- and punctuation-insensitive key: `vendor_name`, `vendorName` and
 * "Vendor Name" all become `vendorname` — server field names and CSV headers
 * are matched through it.
 */
export const norm = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The guard every CSV import dialog puts on a picked file: `.csv` only, and
 * at most `maxBytes` when given. The message to show, or null when it passes.
 */
export const csvFileError = (f: File, maxBytes?: number): string | null => {
  if (!/\.csv$/i.test(f.name)) return 'File harus berformat .csv';
  if (maxBytes !== undefined && f.size > maxBytes)
    return `Ukuran file maksimal ${maxBytes / (1024 * 1024)} MB`;
  return null;
};

/** Guard, then read: the file's text, or the message to show. */
export const readCsvFile = async (
  f: File,
  maxBytes?: number
): Promise<{ text: string; error: null } | { text: null; error: string }> => {
  const error = csvFileError(f, maxBytes);
  return error ? { text: null, error } : { text: await f.text(), error: null };
};

export interface CsvLine {
  /** 1-based physical line where the row starts (what a sheet shows). */
  line: number;
  cells: string[];
}

/**
 * Parse CSV text into rows of cells (quoted fields, escaped quotes, CRLF,
 * a leading BOM), each with the line it starts on. Rows whose every cell is
 * blank are dropped — spreadsheet exports pad sheets with them.
 */
export const parseCsvLines = (text: string): CsvLine[] => {
  const rows: CsvLine[] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let line = 1;
  let rowLine = 1;
  const src = text.replace(/^\uFEFF/, '');

  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    if (row.some((c) => c.trim() !== ''))
      rows.push({ line: rowLine, cells: row });
    row = [];
  };

  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    const newline = ch === '\n' || ch === '\r';
    if (newline && ch === '\r' && src[i + 1] === '\n') i += 1;
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 1;
        } else inQuotes = false;
      } else field += newline ? '\n' : ch;
      if (newline) line += 1;
    } else if (ch === '"') inQuotes = true;
    else if (ch === ',') pushField();
    else if (newline) {
      pushField();
      pushRow();
      line += 1;
      rowLine = line;
    } else field += ch;
  }
  pushField();
  pushRow();
  return rows;
};

/** {@link parseCsvLines} without the line numbers. */
export const parseCsvRows = (text: string): string[][] =>
  parseCsvLines(text).map((r) => r.cells);

/**
 * Parse CSV text into header-keyed records (first non-blank row = header,
 * keys lower-cased and trimmed).
 */
export const parseCsv = (text: string): Record<string, string>[] => {
  const rows = parseCsvRows(text);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  return rows.slice(1).map((cells) => {
    const rec: Record<string, string> = {};
    headers.forEach((h, idx) => {
      rec[h] = (cells[idx] ?? '').trim();
    });
    return rec;
  });
};

/** Koli per TRD: 1 for weighed/volume UoM, otherwise the unit count. */
export const koliFor = (uom: string, qty: number): number =>
  ['kg', 'liter'].includes(uom.toLowerCase()) ? 1 : qty;
